import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError, fmtNGN, fmtUSD, img, tierFor, tierStyle } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Sparkles, ShoppingBag, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";

export default function ArtworkDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [art, setArt] = useState(null);
  const [note, setNote] = useState(null);
  const [noteLoading, setNoteLoading] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    api.get(`/gallery/${id}`).then((r) => setArt(r.data)).catch(() => nav("/gallery"));
  }, [id, nav]);

  const getNote = async () => {
    setNoteLoading(true);
    try {
      const { data } = await api.get(`/ai/curator/${id}`);
      setNote(data.note);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setNoteLoading(false); }
  };

  const buy = async () => {
    setBuying(true);
    try {
      await api.post(`/gallery/${id}/purchase`);
      await refresh();
      toast.success("Artwork added to your Holdings!");
      setConfirm(false);
      nav("/holdings");
    } catch (e) { toast.error(formatApiError(e)); setConfirm(false); }
    finally { setBuying(false); }
  };

  if (!art) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;

  return (
    <div>
      <button onClick={() => nav(-1)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"><ArrowLeft className="h-4 w-4" /> Back</button>
      <div className="grid lg:grid-cols-2 gap-8">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-card border border-border rounded-3xl overflow-hidden">
          <img src={art.image} alt={art.title} className="w-full object-contain max-h-[70vh] bg-muted" />
        </motion.div>
        <div>
          <div className="flex items-center gap-2">
            <p className="text-xs tracking-[0.2em] uppercase text-gold font-bold">{art.category}</p>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${tierStyle(art.tier || tierFor(art.price_ngn))}`} data-testid="detail-tier-badge">{art.tier || tierFor(art.price_ngn)}</span>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold mt-2">{art.title}</h1>
          <p className="text-muted-foreground mt-1">{art.artist}</p>
          {art.description && <p className="text-sm text-muted-foreground mt-4">{art.description}</p>}

          <div className="bg-card border border-border rounded-3xl p-6 mt-6">
            <div className="flex items-baseline gap-3">
              <span className="font-heading text-4xl font-bold">{fmtNGN(art.price_ngn)}</span>
              <span className="text-muted-foreground">{fmtUSD(art.price_usd)}</span>
            </div>
            {art.status === "available" ? (
              <Button onClick={() => setConfirm(true)} data-testid="buy-artwork-btn" className="w-full h-12 rounded-full navy-gradient text-white font-semibold mt-5">
                <ShoppingBag className="h-4 w-4 mr-2" /> Purchase Artwork
              </Button>
            ) : (
              <div className="mt-5 flex items-center gap-2 text-sm text-muted-foreground"><CheckCircle2 className="h-4 w-4" /> This artwork has been collected.</div>
            )}
            <p className="text-xs text-muted-foreground mt-3">Paid from your Growth Wallet. Add funds if needed.</p>
          </div>

          <div className="bg-card border border-border rounded-3xl p-6 mt-4">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-semibold flex items-center gap-2"><Sparkles className="h-4 w-4 text-gold" /> AI Curator's Note</h3>
              {!note && <Button size="sm" variant="outline" className="rounded-full" onClick={getNote} disabled={noteLoading} data-testid="ai-curator-btn">{noteLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reveal"}</Button>}
            </div>
            {note && <p className="text-sm text-muted-foreground mt-3 italic">"{note}"</p>}
          </div>
        </div>
      </div>

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Confirm purchase</AlertDialogTitle>
            <AlertDialogDescription>
              Purchase "{art.title}" for {fmtNGN(art.price_ngn)} from your Growth Wallet?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); buy(); }} disabled={buying} data-testid="confirm-buy-btn" className="rounded-full navy-gradient text-white font-semibold">
              {buying ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
